import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Ship, Truck, Shield, Phone, CalendarPlus, MessageCircle, MapPin, Globe, Building2, ArrowRight, Zap, Lock, Users } from 'lucide-react';
import api, { API_URL } from '../lib/api';
import PickupScheduler from '../components/PickupScheduler';
import LoginForm from '../components/LoginForm';
import WhatsAppFloat from '../components/WhatsAppFloat';
import { useTenant } from '../contexts/TenantContext';

const SERVICES = [
  { icon: Ship, title: 'Embarque Maritimo', desc: 'Contenedores USA a RD', color: 'text-cyan-400' },
  { icon: Truck, title: 'Recogida Gratis', desc: 'Pasamos por tu casa', color: 'text-orange-400' },
  { icon: Shield, title: 'Seguro Incluido', desc: 'Tu carga protegida', color: 'text-green-400' },
  { icon: Globe, title: 'Rastreo Online', desc: 'Sigue tu envio 24/7', color: 'text-blue-400' },
];

// ========== PLATFORM LANDING (Global Metafora) ==========
const PlatformLanding = ({ tenants, onSelectTenant, onLoginDirect }) => {
  const [demoRequest, setDemoRequest] = useState({ nombre: '', email: '', empresa: '', telefono: '', mensaje: '' });
  const [showDemo, setShowDemo] = useState(false);

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    alert('Solicitud enviada correctamente. Un representante se comunicara contigo pronto.');
    setDemoRequest({ nombre: '', email: '', empresa: '', telefono: '', mensaje: '' });
    setShowDemo(false);
  };

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      {/* Top bar */}
      <div className="bg-[#0d1e3a] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center font-black text-lg">G</div>
            <div>
              <h1 className="text-lg font-black tracking-tight leading-none">GLOBAL METAFORA APP</h1>
              <p className="text-[10px] text-blue-300/60 tracking-widest uppercase">Sistema Operativo Empresarial</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="tel:+17189161401" className="hidden sm:flex items-center gap-1.5 text-sm text-white/60 hover:text-white">
              <Phone className="h-3.5 w-3.5" /> 718-916-1401
            </a>
            <Button onClick={onLoginDirect} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 bg-transparent text-xs">
              Acceso Admin
            </Button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 via-transparent to-[#0a1628]" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute top-40 right-1/4 w-72 h-72 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-16 sm:py-24 text-center">
          <p className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-4">+20 Anos de Experiencia</p>
          <h2 className="text-4xl sm:text-6xl font-black leading-tight mb-6">
            Sistemas Operativos<br />
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">Integrales para Empresas</span>
          </h2>
          <p className="text-lg sm:text-xl text-white/50 max-w-2xl mx-auto mb-8">
            Desarrollamos soluciones tecnologicas personalizadas que transforman la manera en que las empresas operan. Seguridad, velocidad y control total.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => setShowDemo(true)} className="h-14 px-8 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-lg font-bold rounded-xl shadow-xl shadow-blue-600/20" data-testid="request-demo-btn">
              Solicitar Demo
            </Button>
            <Button onClick={() => document.getElementById('empresas')?.scrollIntoView({ behavior: 'smooth' })} variant="outline" className="h-14 px-8 border-2 border-white/20 text-white hover:bg-white/10 text-lg font-bold rounded-xl bg-transparent">
              Ver Empresas
            </Button>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Zap, title: 'Rendimiento', desc: 'Sistemas optimizados para maxima velocidad', color: 'from-yellow-500 to-orange-500' },
            { icon: Lock, title: 'Seguridad', desc: 'Datos protegidos con cifrado avanzado', color: 'from-green-500 to-emerald-500' },
            { icon: Users, title: 'Multi-empresa', desc: 'Gestiona multiples empresas desde un panel', color: 'from-blue-500 to-cyan-500' },
          ].map((f, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/8 transition-all group">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <f.icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold mb-1">{f.title}</h3>
              <p className="text-white/40 text-sm">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Empresas Section */}
      <div id="empresas" className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <p className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-2">Nuestros Clientes</p>
          <h3 className="text-3xl sm:text-4xl font-black">Empresas que Confian en Nosotros</h3>
          <p className="text-white/40 mt-2">Selecciona una empresa para ver su sistema en funcionamiento</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tenants.map((t) => (
            <div key={t.id}
              onClick={() => onSelectTenant(t)}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 cursor-pointer hover:bg-white/10 hover:border-blue-500/30 transition-all group"
              data-testid={`tenant-card-${t.id}`}
            >
              <div className="flex items-center gap-4 mb-4">
                {t.logo_url ? (
                  <img src={`${API_URL.replace('/api', '')}${t.logo_url}`} alt={t.nombre} className="h-16 w-16 object-contain rounded-xl bg-white p-1" />
                ) : (
                  <div className="h-16 w-16 rounded-xl flex items-center justify-center text-2xl font-black text-white" style={{ backgroundColor: t.colores?.primary || '#3b82f6' }}>
                    {t.nombre?.charAt(0)}
                  </div>
                )}
                <div>
                  <h4 className="text-lg font-bold group-hover:text-blue-400 transition-colors">{t.nombre}</h4>
                  <p className="text-xs text-white/40">{t.slogan || t.dominio}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {Object.entries(t.colores || {}).slice(0, 3).map(([key, color]) => (
                    <div key={key} className="w-5 h-5 rounded-full border border-white/20" style={{ backgroundColor: color }} title={key} />
                  ))}
                </div>
                <span className="text-blue-400 text-sm font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Entrar <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Login Section */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-2">Acceso al Sistema</h3>
              <p className="text-white/40 mb-4">Si ya tienes una cuenta, inicia sesion directamente. El sistema detectara tu empresa automaticamente.</p>
              <div className="flex flex-wrap gap-3 text-white/30 text-xs">
                <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> Conexion segura</span>
                <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Datos cifrados</span>
                <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> Acceso global</span>
              </div>
            </div>
            <LoginForm companyInfo={{ nombre: 'Global Metafora App' }} />
          </div>
        </div>
      </div>

      {/* Demo Request Modal */}
      {showDemo && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowDemo(false)}>
          <div className="bg-[#0d1e3a] border border-white/10 rounded-2xl p-6 sm:p-8 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="demo-modal">
            <h3 className="text-2xl font-bold mb-1">Solicitar Demo</h3>
            <p className="text-white/40 text-sm mb-6">Completa tus datos y un representante se comunicara contigo para mostrarte el sistema.</p>
            <form onSubmit={handleDemoSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="demo-nombre" value={demoRequest.nombre} onChange={e => setDemoRequest({...demoRequest, nombre: e.target.value})} placeholder="Tu nombre *" className="bg-white/10 border-white/20 text-white placeholder:text-white/30" required />
                <Input data-testid="demo-email" type="email" value={demoRequest.email} onChange={e => setDemoRequest({...demoRequest, email: e.target.value})} placeholder="Email *" className="bg-white/10 border-white/20 text-white placeholder:text-white/30" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input value={demoRequest.empresa} onChange={e => setDemoRequest({...demoRequest, empresa: e.target.value})} placeholder="Nombre de tu empresa" className="bg-white/10 border-white/20 text-white placeholder:text-white/30" />
                <Input value={demoRequest.telefono} onChange={e => setDemoRequest({...demoRequest, telefono: e.target.value})} placeholder="Telefono" className="bg-white/10 border-white/20 text-white placeholder:text-white/30" />
              </div>
              <textarea value={demoRequest.mensaje} onChange={e => setDemoRequest({...demoRequest, mensaje: e.target.value})} placeholder="Cuentanos sobre tu negocio..." className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/30 rounded-md p-3 text-sm min-h-[80px]" />
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setShowDemo(false)} className="flex-1 border-white/20 text-white hover:bg-white/10 bg-transparent">Cancelar</Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 font-bold" data-testid="demo-submit">Enviar Solicitud</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-white/10 mt-8">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center font-black text-xs">G</div>
            <span className="text-white/40 text-sm">Global Metafora App - Ing. Eriberto Fernandez Aquino</span>
          </div>
          <div className="flex items-center gap-4 text-white/30 text-xs">
            <a href="tel:+17189161401" className="hover:text-white">718-916-1401</a>
            <span>820 E 168TH ST BRONX NY 10459</span>
            <a href="https://globalmetafora.com" target="_blank" rel="noreferrer" className="hover:text-white">globalmetafora.com</a>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== TENANT LANDING (empresa específica) ==========
const TenantLanding = ({ tenant, companyInfo, onBack }) => {
  const [activeTab, setActiveTab] = useState('tracking');
  const [trackingNum, setTrackingNum] = useState('');
  const navigate = useNavigate();

  const whatsappNum = (tenant?.whatsapp || companyInfo.telefono_whatsapp || '').replace(/[^0-9]/g, '');
  const whatsappMsg = `Hola ${companyInfo.nombre}! Quiero informacion sobre envios a Republica Dominicana`;

  const handleTracking = (e) => {
    e.preventDefault();
    if (!trackingNum.trim()) return;
    navigate(`/tracking/${trackingNum.trim()}`);
  };

  const primaryColor = tenant?.colores?.primary || '#3b82f6';
  const bgColor = tenant?.colores?.background || '#0a0e2a';

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: bgColor }}>
      {/* TOP BAR */}
      <div style={{ background: `linear-gradient(to right, ${primaryColor}, ${tenant?.colores?.secondary || primaryColor})` }}>
        <div className="max-w-6xl mx-auto px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-5">
            <a href={`tel:+${whatsappNum}`} className="flex items-center gap-1.5 font-bold text-sm sm:text-base hover:opacity-80">
              <Phone className="h-4 w-4" /> {companyInfo.telefono || tenant?.telefono}
            </a>
            <a href={`https://wa.me/${whatsappNum}?text=${encodeURIComponent(whatsappMsg)}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 bg-white/20 px-3 py-1 rounded-full text-sm font-bold hover:bg-white/30 transition-colors">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/80 text-xs sm:text-sm hidden sm:block">{companyInfo.horario || tenant?.horario}</span>
            {onBack && (
              <button onClick={onBack} className="text-xs text-white/60 hover:text-white underline">Volver a Global Metafora</button>
            )}
          </div>
        </div>
      </div>

      {/* HERO */}
      <div className="relative overflow-hidden" style={{ minHeight: '300px' }}>
        <div className="absolute inset-0" style={{ backgroundColor: bgColor }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primaryColor}15, transparent)` }} />
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-8 sm:py-12">
          <div className="flex items-center gap-3 sm:gap-4 mb-6">
            {tenant?.logo_url && (
              <img src={`${API_URL.replace('/api', '')}${tenant.logo_url}`} alt="" className="h-16 w-16 sm:h-24 sm:w-24 rounded-xl border-2 border-white/20 shadow-2xl bg-white p-1 object-contain" />
            )}
            <div>
              <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-none">{companyInfo.nombre || tenant?.nombre}</h1>
              <p className="text-sm sm:text-lg font-medium mt-1 opacity-60">{tenant?.slogan || companyInfo.contacto_nombre}</p>
            </div>
          </div>
        </div>
      </div>

      {/* SERVICES BAR */}
      <div className="border-y border-white/10" style={{ backgroundColor: `${bgColor}dd` }}>
        <div className="max-w-6xl mx-auto px-3 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {SERVICES.map((s, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5">
              <s.icon className={`h-8 w-8 ${s.color} flex-shrink-0 mt-0.5`} />
              <div>
                <p className="font-bold text-sm sm:text-base text-white">{s.title}</p>
                <p className="text-xs sm:text-sm text-white/50">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-6xl mx-auto px-3 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <div className="flex gap-2">
              {[
                { id: 'tracking', label: 'Rastrear Envio', icon: Search },
                { id: 'cita', label: 'Agendar Recogida', icon: CalendarPlus },
              ].map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm sm:text-base transition-all ${activeTab === t.id ? 'text-white shadow-lg' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
                  style={activeTab === t.id ? { backgroundColor: primaryColor, boxShadow: `0 10px 25px ${primaryColor}40` } : {}}>
                  <t.icon className="h-4 w-4 sm:h-5 sm:w-5" /> {t.label}
                </button>
              ))}
            </div>

            {activeTab === 'tracking' && (
              <div className="bg-white/5 rounded-2xl border border-white/10 p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold mb-3 flex items-center gap-2"><MapPin className="h-5 w-5" style={{ color: primaryColor }} /> Rastrear tu Envio</h3>
                <p className="text-white/50 text-sm mb-4">Ingresa tu numero de factura para ver el estado de tu envio.</p>
                <form onSubmit={handleTracking} className="flex gap-2">
                  <Input data-testid="public-tracking-input" value={trackingNum} onChange={(e) => setTrackingNum(e.target.value)}
                    placeholder="Numero de factura" className="h-12 text-base bg-white/10 border-white/20 text-white placeholder:text-white/40" />
                  <Button data-testid="public-tracking-btn" type="submit" className="h-12 px-6 font-bold" style={{ backgroundColor: primaryColor }}>
                    <Search className="mr-2 h-4 w-4" /> Buscar
                  </Button>
                </form>
                <div className="text-center py-8 text-white/20">
                  <Truck className="h-16 w-16 mx-auto mb-2" />
                  <p>Ingresa tu numero de factura para ver el estado completo</p>
                </div>
              </div>
            )}

            {activeTab === 'cita' && <PickupScheduler companyInfo={companyInfo} />}
          </div>

          <LoginForm companyInfo={companyInfo} />
        </div>
      </div>

      {whatsappNum && <WhatsAppFloat whatsappNumber={whatsappNum} />}
      <div className="text-center py-4 text-white/20 text-xs">{companyInfo.nombre || tenant?.nombre} - {tenant?.slogan} - {companyInfo.telefono || tenant?.telefono}</div>
    </div>
  );
};

// ========== MAIN LOGIN COMPONENT ==========
const Login = () => {
  const { tenant } = useTenant();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showDirectLogin, setShowDirectLogin] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const navigate = useNavigate();

  // Check if we're on a specific tenant domain
  const isPreviewDomain = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost';

  useEffect(() => {
    // Fetch all active tenants for the platform landing
    if (isPreviewDomain) {
      fetch(`${API_URL}/tenants/config?domain=__list_all__`)
        .catch(() => {});
      // Fetch tenant list (public - no auth needed for display)
      fetch(`${API_URL}/tenants/public-list`)
        .then(r => r.json())
        .then(data => setTenants(data.data || []))
        .catch(() => {});
    }
  }, [isPreviewDomain]);

  useEffect(() => {
    if (selectedTenant) {
      // When a tenant is selected from the platform, use tenant data directly
      const t = selectedTenant;
      setCompanyInfo({
        nombre: t.nombre, contacto_nombre: t.slogan || '',
        telefono: t.telefono || '', telefono_whatsapp: (t.whatsapp || '').replace(/[^0-9]/g, ''),
        horario: t.horario || '', email: t.email || ''
      });
    } else if (!isPreviewDomain && tenant) {
      // On a client domain, try settings first, fallback to tenant data
      api.get('/settings/company/public').then(res => {
        setCompanyInfo(res.data);
      }).catch(() => {
        setCompanyInfo({
          nombre: tenant.nombre, contacto_nombre: tenant.slogan || '',
          telefono: tenant.telefono || '', telefono_whatsapp: (tenant.whatsapp || '').replace(/[^0-9]/g, ''),
          horario: tenant.horario || ''
        });
      });
    }
  }, [selectedTenant, tenant, isPreviewDomain]);

  // If on a client's actual domain, show their landing directly
  if (!isPreviewDomain && tenant) {
    return (
      <TenantLanding
        tenant={tenant}
        companyInfo={companyInfo || { nombre: tenant.nombre, telefono: tenant.telefono, telefono_whatsapp: (tenant.whatsapp || '').replace(/[^0-9]/g, ''), horario: tenant.horario, contacto_nombre: tenant.slogan }}
      />
    );
  }

  // If a tenant is selected from the platform, show their landing
  if (selectedTenant && companyInfo) {
    return (
      <TenantLanding
        tenant={selectedTenant}
        companyInfo={companyInfo}
        onBack={() => { setSelectedTenant(null); setCompanyInfo(null); }}
      />
    );
  }

  // Default: show Global Metafora platform landing
  return (
    <PlatformLanding
      tenants={tenants}
      onSelectTenant={setSelectedTenant}
      onLoginDirect={() => setShowDirectLogin(!showDirectLogin)}
    />
  );
};

export default Login;
